import {
  cardFields,
  createHandshakeGate,
  GlobalPostMessageEventKind,
  type CardViewerConfig,
  type CardViewerRenderedFields,
  type CardViewerToggleMaskError,
  type Maybe,
  type OnCopyToClipboardSuccessPayload,
  type PaymentCardFields,
} from "@highnoteplatform/types";

export type { OnCopyToClipboardSuccessPayload };

import { buildFrame } from "./buildFrame";
import { iframeOrigin, generateUuid } from "./config";
import { establishCardViewerChannel } from "./handshake";
import { handlePostMessage } from "./handlePostMessage";

type FieldsMap = Readonly<
  Record<
    PaymentCardFields,
    Maybe<{
      container: Element;
      frame: HTMLIFrameElement;
      disposeSecureChannel?: () => void;
    }>
  >
>;

const unmount = async (fields: Readonly<FieldsMap>): Promise<void> => {
  Object.values(fields).forEach((field) => {
    if (field) {
      // Tear down the handshake first so no pending timer can call onError
      // after the frames are gone.
      field.disposeSecureChannel?.();
      field.container.replaceChildren("");
    }
  });
};

export const renderFields = async ({
  clientToken,
  paymentCardId,
  elements,
  onError,
  onCopyToClipboardSuccess,
  environment = "test",
  enableClipboard = true,
  maskedOnLoad = true,
  onReady,
}: Readonly<CardViewerConfig>): Promise<CardViewerRenderedFields> => {
  const uuid = generateUuid();
  // Dedupe the parent timeout and the iframe's relayed configure-timeout at
  // their convergence on onError (see HandshakeGate). Only cardNumber runs a
  // channel, so the failure latch is driven by exactly one handshake. relayError
  // is hoisted only to avoid re-wrapping; the gate state is shared via closure.
  const gate = createHandshakeGate();
  const relayError = gate.wrapRelay(onError);
  const fields = cardFields.reduce<FieldsMap>(
    (accum, field) => {
      const container =
        document.querySelector(elements[field].selector) ?? document.body;

      const frame =
        field === "cardNumber"
          ? buildFrame({
              styles: elements[field].styles,
              environment,
              enableClipboard,
              uuid,
              maskedOnLoad,
            })
          : buildFrame({
              field,
              styles: elements[field].styles,
              enableClipboard,
              uuid,
              environment,
            });

      const disposeSecureChannel =
        field === "cardNumber"
          ? establishCardViewerChannel({
              frame,
              uuid,
              clientToken,
              paymentCardId,
              onError: gate.wrapFailure(onError),
              onHandshakeAck: gate.ack,
            })
          : undefined;

      container.append(frame);

      return {
        ...accum,
        [field]: { container, frame, disposeSecureChannel },
      };
    },
    {
      cardNumber: undefined,
      cvv: undefined,
      expirationDate: undefined,
    }
  );

  window.removeEventListener(
    "message",
    handlePostMessage(uuid, relayError, onCopyToClipboardSuccess, onReady)
  );
  window.addEventListener(
    "message",
    handlePostMessage(uuid, relayError, onCopyToClipboardSuccess, onReady)
  );

  return {
    toggleCardNumberMask: (): Maybe<CardViewerToggleMaskError> => {
      const targetWindow = fields.cardNumber?.frame.contentWindow;

      if (targetWindow === null || targetWindow === undefined) {
        return {
          name: "CardViewerToggleMaskError",
          message: "Unable to toggle card number mask.",
        };
      }

      targetWindow.postMessage(
        { __typename: GlobalPostMessageEventKind.TOGGLE_CARD_NUMBER_MASK },
        iframeOrigin
      );

      return undefined;
    },

    unmount: async () => {
      await unmount(fields);
    },
  };
};
